/**
 * GeoSeoEngine — Citation tracker + schema/llms.txt manager. MOCKED data.
 */
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import MarketingShell from "@/components/MarketingShell";
import { Compass, Search, Sparkles, ArrowUpRight, ArrowDownRight, FileCode2, Bot, Globe } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const SHARE_OF_ANSWER = [];

const ENGINES = [
  { id: "chatgpt", label: "ChatGPT", citations: 0, change: 0, color: "#10A37F" },
  { id: "perplexity", label: "Perplexity", citations: 0, change: 0, color: "#1F40DB" },
  { id: "claude", label: "Claude", citations: 0, change: 0, color: "#D97757" },
  { id: "google-ai", label: "Google AI Overview", citations: 0, change: 0, color: "#4285F4" },
];

const TRACKED_QUERIES = [];

const SCHEMA_ISSUES = [];

export default function GeoSeoEngine() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "citations");
  // Sync local state ←→ URL so sidebar sub-items deep-link properly.
  useEffect(() => {
    const urlTab = searchParams.get("tab");
    if (urlTab && urlTab !== tab) setTab(urlTab);
  }, [searchParams, tab]);
  const setActiveTab = (next) => {
    setTab(next);
    const sp = new URLSearchParams(searchParams);
    sp.set("tab", next);
    setSearchParams(sp, { replace: true });
  };

  return (
    <MarketingShell title="GEO / SEO Engine" subtitle="AI citation share + schema + llms.txt management">
      <div data-testid="geo-seo-page">
        {/* Tab switcher */}
        <div className="mkt-segmented" style={{ marginBottom: 18 }}>
          {[
            { k: "citations", l: "Citation share" },
            { k: "queries", l: "Tracked queries" },
            { k: "schema", l: "Schema / JSON-LD" },
            { k: "llms", l: "llms.txt + AI crawler" },
          ].map((t) => (
            <button key={t.k} onClick={() => setActiveTab(t.k)} className={tab === t.k ? "active" : ""} data-testid={`geo-tab-${t.k}`}>
              {t.l}
            </button>
          ))}
        </div>

        {tab === "citations" && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 22 }}>
              {ENGINES.map((e) => (
                <div key={e.id} className="mkt-kpi" data-testid={`engine-${e.id}`}>
                  <div className="mkt-kpi-icon" style={{ background: `${e.color}15`, color: e.color }}>
                    <Compass style={{ width: 14, height: 14 }} />
                  </div>
                  <div className="mkt-kpi-value">{e.citations}</div>
                  <div className="mkt-kpi-label">{e.label}</div>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 600, color: e.change > 0 ? "var(--success)" : "var(--error)" }}>
                    {e.change > 0 ? <ArrowUpRight style={{ width: 11, height: 11 }} /> : <ArrowDownRight style={{ width: 11, height: 11 }} />}
                    {Math.abs(e.change)} this wk
                  </div>
                </div>
              ))}
            </div>
            <div className="mkt-card mkt-card-pad" style={{ marginBottom: 22 }}>
              <h3 className="card-title" style={{ marginBottom: 14 }}>Share of answer · 6 weeks · "best EHR for small primary care practice"</h3>
              {SHARE_OF_ANSWER.length === 0 ? (
                <div data-testid="share-empty" style={{ padding: "40px 10px", textAlign: "center", color: "var(--text-tertiary)", fontSize: 12.5 }}>
                  Add a tracked query and connect the SEO/GEO agent to start measuring share-of-answer.
                </div>
              ) : (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={SHARE_OF_ANSWER}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} unit="%" />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "0.5px solid var(--border)" }} />
                  <Area type="monotone" dataKey="enable" stroke="var(--blue)" fill="var(--blue-soft)" strokeWidth={2.5} />
                  <Area type="monotone" dataKey="athena" stroke="var(--text-tertiary)" fill="transparent" strokeWidth={1.5} strokeDasharray="3 3" />
                  <Area type="monotone" dataKey="drchrono" stroke="var(--text-tertiary)" fill="transparent" strokeWidth={1.5} strokeDasharray="3 3" />
                  <Area type="monotone" dataKey="tebra" stroke="var(--text-tertiary)" fill="transparent" strokeWidth={1.5} strokeDasharray="3 3" />
                </AreaChart>
              </ResponsiveContainer>
              )}
            </div>
            <div className="ai-banner">
              <div className="ai-pill"><Sparkles style={{ width: 11, height: 11 }} /> SEO / GEO Agent</div>
              <p style={{ fontSize: 12.5 }}>
                No tracked queries yet. Add a query to the agent's watchlist and the SEO / GEO agent will start measuring share-of-answer across ChatGPT, Perplexity, Claude, and Google AI Overview.
              </p>
            </div>
          </>
        )}

        {tab === "queries" && (
          <div className="mkt-table-wrap">
            <table className="mkt-table">
              <thead>
                <tr>{["Query", "Avg rank", "Mentions", "Engines", "Trend"].map(h => <th key={h}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {TRACKED_QUERIES.map((q, i) => (
                  <tr key={i} data-testid={`query-${i}`}>
                    <td style={{ fontSize: 12.5, fontWeight: 500 }}>{q.q}</td>
                    <td className="num">#{q.rank}</td>
                    <td className="num">{q.mentions}</td>
                    <td className="num">{q.engines}/4</td>
                    <td>
                      <span className={`tag ${q.trend === "up" ? "tag-green" : q.trend === "down" ? "tag-red" : "tag-gray"}`}>
                        {q.trend === "up" ? "↑ Rising" : q.trend === "down" ? "↓ Falling" : "→ Flat"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {TRACKED_QUERIES.length === 0 && (
              <div data-testid="queries-empty" style={{ padding: 40, textAlign: "center", color: "var(--text-tertiary)", fontSize: 12.5 }}>
                No tracked queries yet. Add a query to start measuring AI-engine citations.
              </div>
            )}
          </div>
        )}

        {tab === "schema" && (
          <>
            <div className="mkt-card mkt-card-pad" style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 14 }}>
              <FileCode2 style={{ width: 22, height: 22, color: "var(--blue)" }} />
              <div style={{ flex: 1 }}>
                <strong style={{ fontFamily: "var(--font-head)" }}>Wix site detected</strong>
                <p style={{ fontSize: 11.5, color: "var(--text-tertiary)", marginTop: 2 }}>Schema deploys via Wix Velo function · routed through devops on push</p>
              </div>
              <button className="btn">Run audit</button>
            </div>
            <div className="mkt-table-wrap">
              <table className="mkt-table">
                <thead>
                  <tr>{["Page", "Schema type", "Status", "Priority", ""].map(h => <th key={h}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {SCHEMA_ISSUES.map((s, i) => (
                    <tr key={i}>
                      <td style={{ fontSize: 12.5, fontFamily: "var(--font-mono)" }}>{s.page}</td>
                      <td style={{ fontSize: 12 }}>{s.type}</td>
                      <td>
                        <span className={`tag ${s.status === "valid" ? "tag-green" : s.status === "missing" ? "tag-red" : "tag-amber"}`}>
                          {s.status}
                        </span>
                      </td>
                      <td>
                        <span className={`tag ${s.priority === "high" ? "tag-red" : s.priority === "med" ? "tag-amber" : "tag-gray"}`}>{s.priority}</span>
                      </td>
                      <td>
                        {s.status !== "valid" && (
                          <button className="btn btn-primary" style={{ height: 26, fontSize: 11, padding: "0 9px" }}>Generate</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {SCHEMA_ISSUES.length === 0 && (
                <div data-testid="schema-empty" style={{ padding: 40, textAlign: "center", color: "var(--text-tertiary)", fontSize: 12.5 }}>
                  No schema issues detected. Click "Run audit" to scan your site.
                </div>
              )}
            </div>
          </>
        )}

        {tab === "llms" && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}>
              <div className="mkt-card mkt-card-pad">
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <Globe style={{ width: 14, height: 14, color: "var(--blue)" }} />
                  <h3 className="card-title">llms.txt status</h3>
                </div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-secondary)", background: "var(--subtle)", padding: 12, borderRadius: 8, lineHeight: 1.6 }}>
                  # https://enablehealthcare.com/llms.txt<br/>
                  # Enable Healthcare — AI-readable site map<br/>
                  # Last deploy: 2026-02-15<br/><br/>
                  /products/echo-ehr<br/>
                  /products/revq-ai-billing<br/>
                  /products/ccm-platform<br/>
                  /products/rpm<br/>
                  /about<br/>
                  /blog/*
                </div>
                <button className="btn btn-primary" style={{ marginTop: 12 }}>Regenerate</button>
              </div>
              <div className="mkt-card mkt-card-pad">
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <Bot style={{ width: 14, height: 14, color: "var(--blue)" }} />
                  <h3 className="card-title">AI crawler access</h3>
                </div>
                {[
                  { bot: "GPTBot", allowed: true, hits: 0 },
                  { bot: "ClaudeBot", allowed: true, hits: 0 },
                  { bot: "PerplexityBot", allowed: true, hits: 0 },
                  { bot: "OAI-SearchBot", allowed: true, hits: 0 },
                  { bot: "Google-Extended", allowed: true, hits: 0 },
                  { bot: "CCBot", allowed: false, hits: 0 },
                ].map((b) => (
                  <div key={b.bot} style={{ display: "flex", alignItems: "center", padding: "8px 0", borderBottom: "0.5px solid var(--border-soft)" }}>
                    <span style={{ flex: 1, fontFamily: "var(--font-mono)", fontSize: 11.5 }}>{b.bot}</span>
                    <span className="num" style={{ fontSize: 11, color: "var(--text-tertiary)", marginRight: 14 }}>{b.hits.toLocaleString()} hits / 30d</span>
                    <span className={`tag ${b.allowed ? "tag-green" : "tag-gray"}`}>{b.allowed ? "Allowed" : "Blocked"}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </MarketingShell>
  );
}
